import { useState, useEffect } from 'react';
import {
    UsersIcon,
    ArrowTrendingUpIcon,
    DevicePhoneMobileIcon,
    ComputerDesktopIcon,
    FireIcon,
    RocketLaunchIcon,
    BellAlertIcon,
    TrophyIcon,
    ClockIcon
} from '@heroicons/react/24/outline';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer,
    BarChart, Bar,
    PieChart, Pie, Cell,
    AreaChart, Area
} from 'recharts';
import api from '../services/api';

export default function MetricasImpactoAdmin() {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);

    useEffect(() => {
        loadMetrics();
    }, []);

    const loadMetrics = async () => {
        try {
            setLoading(true);
            const response = await api.admin.getMetricasImpacto();
            setData(response);
        } catch (error) {
            console.error('Error cargando métricas:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="w-8 h-8 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    if (!data) return null;

    const COLORS = ['#2dd4bf', '#818cf8', '#f472b6', '#fbbf24', '#f87171'];

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent flex items-center gap-2 sm:gap-3">
                        <RocketLaunchIcon className="w-6 h-6 sm:w-8 sm:h-8 text-cyan-400" />
                        Métricas de Impacto
                    </h1>
                    <p className="text-gray-400 mt-1 text-sm sm:text-base">
                        Monitoriza la adopción de usuarios y el uso de los módulos digitalizados
                    </p>
                </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                <div className="bg-gradient-to-br from-[#1a1a3e]/80 to-[#101026]/90 border border-cyan-500/20 rounded-2xl p-5 shadow-[0_0_15px_rgba(34,211,238,0.1)] transition-transform hover:-translate-y-1">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-400 text-xs uppercase tracking-wider font-semibold mb-1">Usuarios Totales</p>
                            <h3 className="text-3xl font-bold text-white">{data.kpis.totalUsers}</h3>
                        </div>
                        <div className="p-3 bg-cyan-500/20 rounded-xl shadow-[inset_0_0_10px_rgba(34,211,238,0.3)]">
                            <UsersIcon className="w-6 h-6 text-cyan-400" />
                        </div>
                    </div>
                </div>

                <div className="bg-gradient-to-br from-[#1a1a3e]/80 to-[#101026]/90 border border-emerald-500/20 rounded-2xl p-5 shadow-[0_0_15px_rgba(16,185,129,0.1)] transition-transform hover:-translate-y-1">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-400 text-xs uppercase tracking-wider font-semibold mb-1">Usuarios Activos (Mes)</p>
                            <h3 className="text-3xl font-bold text-white">{data.kpis.mau}</h3>
                        </div>
                        <div className="p-3 bg-emerald-500/20 rounded-xl shadow-[inset_0_0_10px_rgba(16,185,129,0.3)]">
                            <ArrowTrendingUpIcon className="w-6 h-6 text-emerald-400" />
                        </div>
                    </div>
                </div>

                <div className="bg-gradient-to-br from-[#1a1a3e]/80 to-[#101026]/90 border border-pink-500/20 rounded-2xl p-5 shadow-[0_0_15px_rgba(236,72,153,0.1)] transition-transform hover:-translate-y-1">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-400 text-xs uppercase tracking-wider font-semibold mb-1">Usuarios Activos (Día)</p>
                            <h3 className="text-3xl font-bold text-white">{data.kpis.dau}</h3>
                        </div>
                        <div className="p-3 bg-pink-500/20 rounded-xl shadow-[inset_0_0_10px_rgba(236,72,153,0.3)]">
                            <FireIcon className="w-6 h-6 text-pink-400" />
                        </div>
                    </div>
                </div>

                <div className="bg-gradient-to-br from-[#1a1a3e]/80 to-[#101026]/90 border border-purple-500/20 rounded-2xl p-5 shadow-[0_0_15px_rgba(168,85,247,0.1)] transition-transform hover:-translate-y-1">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-400 text-xs uppercase tracking-wider font-semibold mb-1">Retención Mensual</p>
                            <h3 className="text-3xl font-bold text-white">{data.kpis.retention}%</h3>
                        </div>
                        <div className="p-3 bg-purple-500/20 rounded-xl shadow-[inset_0_0_10px_rgba(168,85,247,0.3)]">
                            <ArrowTrendingUpIcon className="w-6 h-6 text-purple-400" />
                        </div>
                    </div>
                </div>

                <div className="bg-gradient-to-br from-[#1a1a3e]/80 to-[#101026]/90 border border-amber-500/20 rounded-2xl p-5 shadow-[0_0_15px_rgba(245,158,11,0.1)] transition-transform hover:-translate-y-1">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-400 text-xs uppercase tracking-wider font-semibold mb-1">Cobertura Push</p>
                            <h3 className="text-3xl font-bold text-white">{data.kpis.pushCoverage}%</h3>
                            <p className="text-[10px] text-gray-500 mt-1">{data.kpis.pushEnabledUsers} dispositivos activos</p>
                        </div>
                        <div className="p-3 bg-amber-500/20 rounded-xl shadow-[inset_0_0_10px_rgba(245,158,11,0.3)]">
                            <BellAlertIcon className="w-6 h-6 text-amber-400" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                {/* Peak Hours Area Chart */}
                <div className="bg-[#1a1a3e]/50 border border-white/10 rounded-2xl p-6 shadow-xl lg:col-span-2">
                    <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                        <ClockIcon className="w-5 h-5 text-pink-400" />
                        Horas Pico del Sistema (Prime Time)
                    </h3>
                    <div className="h-[300px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={data.peakHours} margin={{ top: 10, right: 30, left: -20, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="colorActividad" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#f472b6" stopOpacity={0.8} />
                                        <stop offset="95%" stopColor="#f472b6" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                                <XAxis dataKey="hour" stroke="#6b7280" fontSize={11} interval={1} minTickGap={15} />
                                <YAxis stroke="#6b7280" fontSize={12} />
                                <RechartsTooltip
                                    contentStyle={{ backgroundColor: '#0f172a', borderColor: '#334155', borderRadius: '12px', color: '#fff' }}
                                    itemStyle={{ color: '#f472b6' }}
                                    labelStyle={{ color: '#94a3b8' }}
                                    formatter={(value) => [`${value} acciones`, 'Actividad']}
                                />
                                <Area type="monotone" dataKey="actividad" stroke="#f472b6" fillOpacity={1} fill="url(#colorActividad)" animationDuration={1500} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Evolution Line Chart */}
                <div className="bg-[#1a1a3e]/50 border border-white/10 rounded-2xl p-6 shadow-xl">
                    <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                        <ArrowTrendingUpIcon className="w-5 h-5 text-cyan-400" />
                        Conexiones Diarias (Últimos 30 días)
                    </h3>
                    <div className="h-[300px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={data.evolution} margin={{ top: 5, right: 20, bottom: 5, left: -20 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                                <XAxis dataKey="date" stroke="#6b7280" fontSize={12} tickMargin={10} />
                                <YAxis stroke="#6b7280" fontSize={12} />
                                <RechartsTooltip
                                    contentStyle={{ backgroundColor: '#0f172a', borderColor: '#334155', borderRadius: '12px' }}
                                    itemStyle={{ color: '#2dd4bf' }}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="logins"
                                    stroke="#2dd4bf"
                                    strokeWidth={3}
                                    dot={{ fill: '#0f172a', stroke: '#2dd4bf', strokeWidth: 2, r: 4 }}
                                    activeDot={{ r: 6, fill: '#2dd4bf' }}
                                    animationDuration={1500}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Module Usage Bar Chart */}
                <div className="bg-[#1a1a3e]/50 border border-white/10 rounded-2xl p-6 shadow-xl">
                    <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                        <RocketLaunchIcon className="w-5 h-5 text-purple-400" />
                        Uso Acumulado por Módulo
                    </h3>
                    <div className="h-[300px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={data.modules} margin={{ top: 5, right: 20, bottom: 20, left: -20 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                                <XAxis dataKey="name" stroke="#6b7280" fontSize={11} interval={0} tickMargin={10} />
                                <YAxis stroke="#6b7280" fontSize={12} />
                                <RechartsTooltip
                                    cursor={{ fill: 'rgba(255,255,255,0.02)' }}
                                    contentStyle={{ backgroundColor: '#0f172a', borderColor: '#334155', borderRadius: '12px', color: '#fff' }}
                                    itemStyle={{ color: '#e2e8f0' }}
                                />
                                <Bar dataKey="value" radius={[6, 6, 0, 0]} animationDuration={1500}>
                                    {data.modules.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Top Users Ranking */}
                <div className="bg-[#1a1a3e]/50 border border-white/10 rounded-2xl p-6 shadow-xl lg:col-span-2">
                    <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                        <TrophyIcon className="w-5 h-5 text-amber-400" />
                        Top 5 Usuarios Embajadores
                    </h3>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead>
                                <tr className="border-b border-white/10 text-xs uppercase tracking-wider text-gray-400">
                                    <th className="pb-3 px-2 font-medium">Rank</th>
                                    <th className="pb-3 px-2 font-medium">Conductor</th>
                                    <th className="pb-3 px-2 font-medium hidden sm:table-cell">Correo</th>
                                    <th className="pb-3 px-2 font-medium text-right">Interacciones (30d)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.topUsers.map((user) => (
                                    <tr key={user.rank} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                                        <td className="py-4 px-2">
                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm
                                                ${user.rank === 1 ? 'bg-amber-500/20 text-amber-400 ring-1 ring-amber-500/50' :
                                                    user.rank === 2 ? 'bg-gray-300/20 text-gray-300 ring-1 ring-gray-300/50' :
                                                        user.rank === 3 ? 'bg-orange-700/20 text-orange-400 ring-1 ring-orange-700/50' :
                                                            'bg-white/5 text-gray-400'}`}>
                                                #{user.rank}
                                            </div>
                                        </td>
                                        <td className="py-4 px-2 font-medium text-white">{user.name}</td>
                                        <td className="py-4 px-2 text-gray-500 text-sm hidden sm:table-cell">{user.email}</td>
                                        <td className="py-4 px-2 text-right">
                                            <span className="bg-cyan-500/10 text-cyan-400 px-3 py-1 rounded-full font-mono text-sm border border-cyan-500/20">
                                                {user.score}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                                {data.topUsers.length === 0 && (
                                    <tr><td colSpan="4" className="text-center py-6 text-gray-500">Sin datos registrados</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Platform Distribution Pie Chart */}
                <div className="bg-[#1a1a3e]/50 border border-white/10 rounded-2xl p-6 shadow-xl lg:col-span-2">
                    <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                        <DevicePhoneMobileIcon className="w-5 h-5 text-emerald-400" />
                        Distribución por Dispositivo
                    </h3>
                    <div className="flex flex-col md:flex-row items-center justify-center gap-12">
                        <div className="h-[250px] w-[250px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={data.platforms}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={70}
                                        outerRadius={100}
                                        paddingAngle={5}
                                        dataKey="value"
                                        stroke="none"
                                        animationDuration={1500}
                                    >
                                        <Cell fill="#34d399" /> {/* Emerald para móvil */}
                                        <Cell fill="#818cf8" /> {/* Indigo para desktop */}
                                    </Pie>
                                    <RechartsTooltip
                                        contentStyle={{ backgroundColor: '#0f172a', borderColor: '#334155', borderRadius: '12px', color: 'white' }}
                                        itemStyle={{ color: 'white' }}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="flex flex-col gap-6">
                            <div className="flex items-center gap-4 bg-emerald-500/10 border border-emerald-500/20 p-4 rounded-xl min-w-[200px]">
                                <DevicePhoneMobileIcon className="w-8 h-8 text-emerald-400" />
                                <div>
                                    <p className="text-xs text-emerald-400/80 font-semibold uppercase tracking-wider">App Nativa / Móvil</p>
                                    <p className="text-2xl font-bold text-white">{data.platforms[0]?.value || 0}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-4 bg-indigo-500/10 border border-indigo-500/20 p-4 rounded-xl min-w-[200px]">
                                <ComputerDesktopIcon className="w-8 h-8 text-indigo-400" />
                                <div>
                                    <p className="text-xs text-indigo-400/80 font-semibold uppercase tracking-wider">Navegador Web</p>
                                    <p className="text-2xl font-bold text-white">{data.platforms[1]?.value || 0}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
}
